import type { GroupBase, Props, PropsValue, StylesConfig } from 'react-select';
import ReactSelect, { components } from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';
import type { ReactElement } from 'react';
import React, { useRef, useState } from 'react';

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',
  color,
});

const Input = (props: any) => <components.Input {...props} isHidden={false} />;

export function ReactSelectWrapped<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group> & {
    isCreatable?: boolean;
    value: PropsValue<string | { label: string; value: string }> | undefined;
  }
): ReactElement {
  const colourStyles: StylesConfig<Option, IsMulti, Group> = {
    control: styles => ({
      ...styles,
      boxShadow: 'none',
      backgroundColor: '#141414',
      borderColor: '#737373',
      minHeight: 26,
      fontSize: 12,
      ':active': { borderColor: '#FFFFFFAA' },
      ':focus': { borderColor: '#FFFFFFAA' },
      ':hover': { borderColor: '#FFFFFFAA' },
    }),
    option: (styles, { isDisabled, isSelected, isFocused }) => {
      return {
        ...styles,
        backgroundColor: !isSelected && !isFocused ? '#141414' : '#1C1E1F',
        color: isSelected || isFocused ? '#FFFFFF' : '#FFFFFFAA',
        cursor: isDisabled ? 'not-allowed' : 'default',
        fontSize: 12,
        padding: 4,

        ':active': {
          ...styles[':active'],
          backgroundColor: '#141414',
          color: 'white',
        },
      };
    },
    input: styles => ({ ...styles, color: '#FFFFFFAA', margin: 0 }),
    placeholder: styles => ({ ...styles, color: '#737373' }),
    singleValue: styles => ({ ...styles, ...dot('#FFFFFFAA') }),
    indicatorsContainer: styles => ({ ...styles, '> div': { padding: 2 } }),
    valueContainer: styles => ({ ...styles, padding: '0 8px' }),
  };

  const extraProps: Props<Option, IsMulti, Group> & { isCreatable?: boolean } = {
    ...props,
    theme: theme => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#4044ee',
        primary25: '#4044ee',
        primary50: '#4044ee',
        primary75: '#4044ee',
        neutral0: '#141414', // Background
        neutral5: '#FFFFFFAA',
        neutral10: '#FFFFFFAA',
        neutral20: '#FFFFFFAA',
        neutral30: '#FFFFFFAA',
        neutral40: '#FFFFFFAA',
        neutral50: '#FFFFFFAA',
        neutral60: '#FFFFFFAA',
        neutral70: '#FFFFFFAA',
        neutral80: '#FFFFFFAA',
        neutral90: '#FFFFFFAA',
      },
      fontSize: 12,
    }),
    styles: colourStyles,
  };

  // for editable options
  const [inputValue, setInputValue] = useState('');

  const selectRef = useRef(null);

  const onInputChange = (inputValue: string, { action }: { action: string }) => {
    // onBlur => setInputValue to last selected value
    // if (action === "input-blur") {
    //   setInputValue(value ? value.label : "");
    // }

    // onInputChange => update inputValue
    if (action === 'input-change') {
      setInputValue(inputValue);
    }
  };

  const onChange = (option: any, action: any) => {
    if (option && props.onChange) {
      props.onChange(option, action);
    }
    setInputValue(option ? option.label : '');
  };

  const onFocus = () =>
    props.value && setInputValue((props.value as { value: string; label: string }).value);

  if (props.isCreatable && props.isMulti === false) {
    const options = [...(props.options ?? [])] as { label: string; value: string }[];
    const defaultValueOption = props.defaultValue as { label: string; value: string } | undefined;
    if (
      defaultValueOption &&
      defaultValueOption.value &&
      !options.find(option => option.value === defaultValueOption?.value)
    ) {
      options.unshift(defaultValueOption);
    }
    return (
      <ReactSelectCreatable
        {...extraProps}
        ref={selectRef}
        options={options as Option[]}
        onInputChange={onInputChange}
        inputValue={inputValue}
        onChange={onChange}
        isClearable={true}
        onFocus={onFocus}
        components={{
          Input,
        }}
      />
    );
  }
  return <ReactSelect {...extraProps} />;
}
